import React from "react";
import { useParams, Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import PageIntrouvable from "./PageIntrouvable";
import { Button } from "../components/ui/button";
import { articles, trouverArticle } from "../utils/articles";
import { filArianeJsonLd, SITE } from "../utils/donneesStructurees";
import { trackSimulatorClick } from "../utils/analytics";
import { Calendar, Clock, ArrowRight, ArrowLeft } from "lucide-react";

const ArticleBlog = () => {
  const { slug } = useParams();
  const article = trouverArticle(slug);

  // Un slug inconnu doit renvoyer la vraie page 404 en noindex,
  // pas un article vide en HTTP 200.
  if (!article) return <PageIntrouvable />;

  const autres = articles.filter((a) => a.slug !== slug).slice(0, 3);

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.titre,
    description: article.description,
    datePublished: article.date,
    dateModified: article.date,
    inLanguage: "fr-FR",
    mainEntityOfPage: `${SITE}/blog/${article.slug}`,
    author: { "@id": `${SITE}/#entreprise` },
    publisher: { "@id": `${SITE}/#entreprise` },
  };

  return (
    <div className="min-h-screen bg-white">
      <Seo
        titre={article.titre.length > 60 ? `${article.titre.slice(0, 57)}...` : article.titre}
        description={article.description}
        chemin={`/blog/${article.slug}`}
        donnees={[
          articleJsonLd,
          filArianeJsonLd([
            { nom: "Accueil", url: "/" },
            { nom: "Guides", url: "/blog" },
            { nom: article.titre, url: `/blog/${article.slug}` },
          ]),
        ]}
      />
      <Header />

      <main id="contenu">

      <article className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <Link
              to="/blog"
              className="inline-flex items-center min-h-[44px] text-orange-700 hover:text-orange-800 mb-6"
            >
              <ArrowLeft className="mr-2 h-4 w-4" aria-hidden="true" />
              Tous les guides
            </Link>

            <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4 leading-tight">
              {article.titre}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-sm text-gray-700 mb-8">
              <span className="inline-flex items-center">
                <Calendar className="h-4 w-4 mr-1" aria-hidden="true" />
                <time dateTime={article.date}>
                  {new Date(article.date).toLocaleDateString("fr-FR", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })}
                </time>
              </span>
              <span className="inline-flex items-center">
                <Clock className="h-4 w-4 mr-1" aria-hidden="true" />
                {article.lecture} de lecture
              </span>
            </div>

            <p className="text-xl text-gray-700 leading-relaxed mb-12 pb-8 border-b border-gray-200">
              {article.resume}
            </p>

            {article.sections.map((s) => (
              <section key={s.titre} className="mb-10">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">{s.titre}</h2>
                {s.paragraphes.map((p, i) => (
                  <p key={i} className="text-lg text-gray-700 leading-relaxed mb-4">
                    {p}
                  </p>
                ))}
              </section>
            ))}

            <div className="bg-orange-50 border border-orange-200 rounded-xl p-8 mt-12">
              <h2 className="text-xl font-bold text-gray-900 mb-3">
                Savoir où vous en êtes en quelques minutes
              </h2>
              <p className="text-gray-700 mb-6">
                Le simulateur vous indique votre taux de prise en charge et le montant
                d'aide auquel vous pouvez prétendre.
              </p>
              <Link to="/simulateur" onClick={() => trackSimulatorClick(`article_${article.slug}`)}>
                <Button className="bg-orange-700 hover:bg-orange-800 text-white">
                  Tester mon éligibilité
                  <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />
                </Button>
              </Link>
            </div>

            {autres.length > 0 && (
              <div className="mt-16 pt-8 border-t border-gray-200">
                <h2 className="text-xl font-bold text-gray-900 mb-6">À lire aussi</h2>
                <ul className="space-y-3">
                  {autres.map((a) => (
                    <li key={a.slug}>
                      <Link
                        to={`/blog/${a.slug}`}
                        className="inline-flex items-center min-h-[44px] text-orange-700 hover:text-orange-800 underline"
                      >
                        {a.titre}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </article>

      </main>

      <Footer />
    </div>
  );
};

export default ArticleBlog;
